const CART_DRAWER_SLIDE_GAP = 8;
const CART_DRAWER_SLIDES_PER_VIEW = 1.8;

const CART_DRAWER_RECOMMENDATION_SOURCE = 'cart drawer';

function updateCartDrawerSlidesOffsetAfter(swiper) {
  if (!swiper?.slides?.length || !swiper.width) return;

  const slideSize = swiper.slides[0]?.swiperSlideSize || 0;
  if (!slideSize) return;

  const offset = Math.max(0, swiper.width - slideSize);
  if (swiper.params.slidesOffsetAfter === offset) return;

  swiper.params.slidesOffsetAfter = offset;
  swiper.update();
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define(
    'cart-drawer-recommendations',
    class CartDrawerRecommendations extends HTMLElement {
      connectedCallback() {
        if (!this.dataset.productId || this.dataset.loaded === 'true') return;

        const cartDrawer = this.closest('cart-drawer');
        if (cartDrawer?.classList.contains('active')) {
          this.loadRecommendations(this.dataset.productId);
        }
      }

      disconnectedCallback() {
        this.destroySwiper();
      }

      loadRecommendations(productId) {
        if (!productId || this.dataset.loaded === 'true' || this.dataset.loading === 'true') return;

        this.dataset.loading = 'true';

        fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
          .then((response) => response.text())
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const content =
              doc.querySelector('.cart-drawer-recommendations-content') ||
              doc.querySelector(`#shopify-section-${this.dataset.sectionId} .cart-drawer-recommendations-content`);

            if (!content?.innerHTML.trim()) {
              this.destroySwiper();
              this.remove();
              return;
            }

            this.destroySwiper();
            this.innerHTML = content.innerHTML;
            this.dataset.loaded = 'true';
            this.bindAddButtons();
            this.scheduleSwiperInit();
          })
          .catch((error) => {
            console.error(error);
            this.destroySwiper();
            this.remove();
          })
          .finally(() => {
            delete this.dataset.loading;
          });
      }

      destroySwiper() {
        clearTimeout(this.initTimer);
        this.initTimer = null;
        this.swiperResizeObserver?.disconnect();
        this.swiperResizeObserver = null;

        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
      }

      scheduleSwiperInit() {
        clearTimeout(this.initTimer);
        this.initTimer = setTimeout(() => this.initSwiper(), 50);
      }

      refreshSlider() {
        this.destroySwiper();
        this.scheduleSwiperInit();
      }

      initSwiper() {
        const swiperEl = this.querySelector('.cart-drawer-recommendations__swiper');
        if (!swiperEl) return;

        const slides = swiperEl.querySelectorAll('.swiper-slide');
        const prevBtn = this.querySelector('.cart-drawer-recommendations__nav-btn--prev');
        const nextBtn = this.querySelector('.cart-drawer-recommendations__nav-btn--next');

        if (slides.length <= 1) return;

        const tryInit = (attempt = 0) => {
          if (typeof Swiper === 'undefined') {
            if (attempt < 40) requestAnimationFrame(() => tryInit(attempt + 1));
            return;
          }

          const cartDrawer = this.closest('cart-drawer');
          const containerWidth = swiperEl.getBoundingClientRect().width;

          if (!cartDrawer?.classList.contains('active') || containerWidth <= 0 || containerWidth > 800) {
            if (attempt < 40) requestAnimationFrame(() => tryInit(attempt + 1));
            return;
          }

          this.destroySwiper();

          this.swiper = new Swiper(swiperEl, {
            slidesPerView: CART_DRAWER_SLIDES_PER_VIEW,
            spaceBetween: CART_DRAWER_SLIDE_GAP,
            speed: 350,
            grabCursor: true,
            simulateTouch: true,
            allowTouchMove: true,
            resistanceRatio: 0.85,
            threshold: 8,
            touchStartPreventDefault: false,
            nested: true,
            touchReleaseOnEdges: true,
            observer: true,
            observeParents: true,
            watchOverflow: true,
            navigation:
              prevBtn && nextBtn
                ? {
                    prevEl: prevBtn,
                    nextEl: nextBtn,
                  }
                : undefined,
            on: {
              init: (swiper) => updateCartDrawerSlidesOffsetAfter(swiper),
              resize: (swiper) => updateCartDrawerSlidesOffsetAfter(swiper),
            },
          });

          requestAnimationFrame(() => {
            this.swiper?.updateSize();
            this.swiper?.updateSlides();
            updateCartDrawerSlidesOffsetAfter(this.swiper);
            this.swiper?.slideTo(0, 0);
          });

          this.swiperResizeObserver = new ResizeObserver(() => {
            if (!this.swiper) return;
            this.swiper.updateSize();
            this.swiper.updateSlides();
            updateCartDrawerSlidesOffsetAfter(this.swiper);
          });
          this.swiperResizeObserver.observe(swiperEl);
        };

        tryInit();
      }

      bindAddButtons() {
        this.cart = document.querySelector('cart-drawer');
        this.querySelectorAll('[data-recommendation-add]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.onAddClick(button);
          });
        });
      }

      onAddClick(button) {
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        this.addToCart(button);
      }

      addToCart(button) {
        button.setAttribute('aria-disabled', true);
        button.classList.add('loading');
        button.querySelector('.loading__spinner')?.classList.remove('hidden');

        const formData = new FormData();
        formData.append('id', button.dataset.variantId);
        formData.append('quantity', 1);
        formData.append('properties[_source]', CART_DRAWER_RECOMMENDATION_SOURCE);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(button);
        }

        const config = fetchConfig('javascript');
        delete config.headers['Content-Type'];
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) return;

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-drawer-recommendations',
              cartData: response,
            });

            this.cart.renderContents(response);
            if (this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }

            loadCartDrawerRecommendations(this.cart, { force: true });
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            button.classList.remove('loading');
            button.removeAttribute('aria-disabled');
            button.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }
    }
  );
}

function loadCartDrawerRecommendations(cartDrawer, { force = false } = {}) {
  if (!cartDrawer) return;

  cartDrawer.querySelectorAll('cart-drawer-recommendations').forEach((element) => {
    if (force) {
      element.destroySwiper?.();
      delete element.dataset.loaded;
      delete element.dataset.loading;
    }

    element.loadRecommendations(element.dataset.productId);
  });
}

function refreshCartDrawerRecommendationSliders(cartDrawer) {
  if (!cartDrawer) return;

  cartDrawer.querySelectorAll('cart-drawer-recommendations').forEach((element) => {
    element.refreshSlider?.();
  });
}
